import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Fade,
    LinearProgress,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import TokenIcon from '@mui/icons-material/Token';
import InfoIcon from '@mui/icons-material/Info';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useTranslation } from 'react-i18next';
import { apiRequest, getApiSessionToken } from '../../Utils/apiClient';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';

const formatNumber = (value) => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat().format(numericValue);
};

const createEmptyTokenUsage = (period = 'month') => ({
    period,
    series: [],
    projects: [],
    totals: {
        input_tokens: 0,
        output_tokens: 0,
        total_tokens: 0,
        request_count: 0
    }
});

const normalizeTokenUsagePayload = (payload = {}, fallbackPeriod = 'month') => ({
    period: payload.period || fallbackPeriod,
    series: Array.isArray(payload.series) ? payload.series : [],
    projects: Array.isArray(payload.projects) ? payload.projects : [],
    totals: payload.totals || {
        input_tokens: 0,
        output_tokens: 0,
        total_tokens: 0,
        request_count: 0
    }
});

const ModelUsagesSettings = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isVisible, setIsVisible] = useState(false);
    const [tokenUsagePeriod, setTokenUsagePeriod] = useState('month');
    const [tokenUsageLoading, setTokenUsageLoading] = useState(false);
    const [tokenUsage, setTokenUsage] = useState(() => createEmptyTokenUsage());

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        let isActive = true;

        const loadUsage = async () => {
            const sessionToken = getApiSessionToken();
            if (!sessionToken) {
                if (isActive) {
                    setTokenUsage(createEmptyTokenUsage(tokenUsagePeriod));
                    setTokenUsageLoading(false);
                }
                return;
            }

            if (isActive) {
                setTokenUsageLoading(true);
            }

            try {
                const payload = await apiRequest(`/api/v1/admin/ai-token-usage?group_by=${encodeURIComponent(tokenUsagePeriod)}`);
                if (!isActive) {
                    return;
                }
                setTokenUsage(normalizeTokenUsagePayload(payload, tokenUsagePeriod));
            } catch (error) {
                if (!isActive) {
                    return;
                }
                console.error('Error loading global AI token usage:', error);
                setTokenUsage(createEmptyTokenUsage(tokenUsagePeriod));
            } finally {
                if (isActive) {
                    setTokenUsageLoading(false);
                }
            }
        };

        loadUsage();

        return () => {
            isActive = false;
        };
    }, [tokenUsagePeriod]);

    const handlePeriodChange = (_, value) => {
        if (value) {
            setTokenUsagePeriod(value);
        }
    };

    const totals = useMemo(() => ({
        input_tokens: Number(tokenUsage.totals?.input_tokens || 0),
        output_tokens: Number(tokenUsage.totals?.output_tokens || 0),
        total_tokens: Number(tokenUsage.totals?.total_tokens || 0),
        request_count: Number(tokenUsage.totals?.request_count || 0)
    }), [tokenUsage.totals]);

    const chartData = useMemo(() => {
        return (tokenUsage.series || []).map((row) => ({
            period: row.period,
            input_tokens: Number(row.input_tokens || 0),
            output_tokens: Number(row.output_tokens || 0),
            total_tokens: Number(row.total_tokens || 0),
            request_count: Number(row.request_count || 0)
        }));
    }, [tokenUsage.series]);

    const projectBreakdown = useMemo(() => {
        return (tokenUsage.projects || []).map((project) => ({
            project_id: project.project_id,
            project_title: project.project_title || t('global_setting.models_usages_unknown_project', 'Unknown project'),
            input_tokens: Number(project.input_tokens || 0),
            output_tokens: Number(project.output_tokens || 0),
            total_tokens: Number(project.total_tokens || 0),
            request_count: Number(project.request_count || 0)
        }));
    }, [tokenUsage.projects, t]);

    const topProjectTotal = projectBreakdown[0]?.total_tokens || 0;

    return (
        <>
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}
                    >
                        {t('global_setting.models_usages_title', 'Models usages')}
                        <Tooltip title={t('global_setting.models_usages_description', 'Aggregate token usage across all projects')}>
                            <InfoIcon fontSize="small" />
                        </Tooltip>
                    </Typography>
                </Box>
            </Fade>

            <Fade in={isVisible} timeout={800}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '1100px',
                        mx: 'auto',
                        px: { xs: 0, sm: 1.5 }
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            width: '100%',
                            p: { xs: 2.5, sm: 4 },
                            borderRadius: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                            border: '1px solid rgba(87, 87, 176, 0.08)',
                            boxShadow: '0 4px 20px rgba(87, 87, 176, 0.08)',
                            mb: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                                    boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                                    flexShrink: 0
                                }}
                            >
                                <TokenIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 240 }}>
                                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                                    {t('global_setting.models_usages_summary', 'Global token usage')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                                    {t('global_setting.models_usages_subtitle', 'Input, output, and total tokens across all projects')}
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={tokenUsagePeriod}
                                onChange={handlePeriodChange}
                                sx={{
                                    ml: 'auto',
                                    '& .MuiToggleButton-root': {
                                        px: { xs: 1, sm: 1.5 },
                                        textTransform: 'none'
                                    }
                                }}
                            >
                                <ToggleButton value="day">{t('settings.ai.token_usage_day', 'Day')}</ToggleButton>
                                <ToggleButton value="week">{t('settings.ai.token_usage_week', 'Week')}</ToggleButton>
                                <ToggleButton value="month">{t('settings.ai.token_usage_month', 'Month')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 3 }}>
                            {[
                                ['input_tokens', t('settings.ai.token_usage_input', 'Input')],
                                ['output_tokens', t('settings.ai.token_usage_output', 'Output')],
                                ['total_tokens', t('settings.ai.token_usage_total', 'Total')],
                                ['request_count', t('settings.ai.token_usage_requests', 'Requests')],
                            ].map(([key, label]) => (
                                <Box
                                    key={key}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        border: '1px solid rgba(87, 87, 176, 0.10)',
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 1px 8px rgba(15, 23, 42, 0.03)',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', letterSpacing: '0.02em' }}>
                                        {label}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>
                                        {formatNumber(totals[key])}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Box
                            sx={{
                                width: '100%',
                                height: { xs: 280, sm: 340 },
                                position: 'relative',
                                borderRadius: 2,
                                border: '1px solid rgba(87, 87, 176, 0.10)',
                                background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 1) 100%)',
                                p: { xs: 1, sm: 1.5 },
                            }}
                        >
                            {tokenUsageLoading ? (
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.30)" />
                                        <XAxis
                                            dataKey="period"
                                            tick={{ fontSize: 11, fill: '#475569' }}
                                            interval="preserveStartEnd"
                                            minTickGap={12}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: '#475569' }}
                                            tickFormatter={(value) => {
                                                const numericValue = Number(value || 0);
                                                if (numericValue >= 1000000) return `${(numericValue / 1000000).toFixed(1)}M`;
                                                if (numericValue >= 1000) return `${Math.round(numericValue / 1000)}K`;
                                                return String(numericValue);
                                            }}
                                        />
                                        <RechartsTooltip
                                            contentStyle={{
                                                borderRadius: 12,
                                                border: '1px solid rgba(87, 87, 176, 0.14)',
                                                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.10)',
                                            }}
                                            formatter={(value, name) => {
                                                const labels = {
                                                    input_tokens: t('settings.ai.token_usage_input', 'Input'),
                                                    output_tokens: t('settings.ai.token_usage_output', 'Output'),
                                                    total_tokens: t('settings.ai.token_usage_total', 'Total'),
                                                    request_count: t('settings.ai.token_usage_requests', 'Requests')
                                                };
                                                return [formatNumber(value), labels[name] || name];
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="input_tokens" name={t('settings.ai.token_usage_input', 'Input')} fill="#0f766e" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="output_tokens" name={t('settings.ai.token_usage_output', 'Output')} fill="#d97706" radius={[4, 4, 0, 0]} />
                                        <Line type="monotone" dataKey="total_tokens" name={t('settings.ai.token_usage_total', 'Total')} stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
                                        {t('global_setting.models_usages_empty', 'No token usage has been recorded yet.')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            width: '100%',
                            p: { xs: 2.5, sm: 4 },
                            borderRadius: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #ffffff 0%, #fafafe 100%)',
                            border: '1px solid rgba(87, 87, 176, 0.08)',
                            boxShadow: '0 4px 20px rgba(87, 87, 176, 0.08)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                            <BarChartIcon sx={{ color: '#5757b0' }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748' }}>
                                    {t('global_setting.models_usages_by_project', 'Usage by project')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                    {t('global_setting.models_usages_by_project_subtitle', 'Projects ranked by total token usage')}
                                </Typography>
                            </Box>
                        </Box>

                        {tokenUsageLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : projectBreakdown.length > 0 ? (
                            <Stack spacing={1.5}>
                                {projectBreakdown.slice(0, 12).map((project) => {
                                    const progressValue = topProjectTotal > 0 ? (project.total_tokens / topProjectTotal) * 100 : 0;
                                    return (
                                        <Box
                                            key={project.project_id}
                                            sx={{
                                                border: '1px solid rgba(87, 87, 176, 0.10)',
                                                borderRadius: 2,
                                                p: 1.5,
                                                backgroundColor: '#fff'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1 }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }} noWrap>
                                                        {project.project_title}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                        {t('global_setting.models_usages_project_id', 'Project #{{projectId}}', { projectId: project.project_id })} · {formatNumber(project.request_count)} {t('settings.ai.token_usage_requests', 'Requests')}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={formatNumber(project.total_tokens)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#eef2ff',
                                                        color: '#3730a3',
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(progressValue, 100)}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 999,
                                                    backgroundColor: '#e5e7eb',
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 999,
                                                        background: 'linear-gradient(90deg, #5757b0 0%, #7c83f5 100%)'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                                {projectBreakdown.length > 12 && (
                                    <Typography variant="caption" sx={{ color: '#64748b', pt: 0.5 }}>
                                        {t('global_setting.models_usages_more_projects', 'Showing the top 12 projects out of {{count}}', { count: projectBreakdown.length })}
                                    </Typography>
                                )}
                            </Stack>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
                                    {t('global_setting.models_usages_empty_projects', 'No projects have recorded token usage yet.')}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Fade>
        </>
    );
};

export default ModelUsagesSettings;
